import type { PedidoNaLista } from '@entregas/api';
import { useFilaDoDia } from '@entregas/api/react';
import {
  type FilaDoBalcao,
  filaDoBalcao,
  formatarHora,
  janelaDoDiaOperacional,
  type LinhaDaFila,
  plural,
  primeiroNome,
  textoDoRelogio,
} from '@entregas/core';
import { Fragment, useMemo } from 'react';
import { Link } from 'react-router';
import { useAgora } from '../lib/useAgora';
import { Icone } from '../ui';
import { useConexao } from './conexao';
import css from './LinhaDeEstado.module.css';
import { useLoja } from './loja';

// Linha fixa no topo de toda tela do painel (quadro do balcão, item 2 e 6): quem atrasou, o prazo e
// a hora da última leitura; sem conexão, diz desde quando e que a lista é a última que chegou.

/** Fora da lista (Novo pedido, Clientes...) a moldura relê a fila neste ritmo; a lista relê a 15 s. */
export const INTERVALO_DA_MOLDURA_MS = 30_000;
/** Com mais atrasados que isto, cada um vai só com o 1º nome e os minutos (o pico). */
const ATRASADOS_POR_EXTENSO = 2;

export interface FilaDaMoldura {
  fila: FilaDoBalcao<PedidoNaLista> | null;
  /** Hora da última leitura que deu certo (ISO); null antes da primeira. */
  atualizadoEm: string | null;
}

/** A fila do dia que a moldura mostra (menu e linha de estado): a mesma consulta da lista. */
export function useFilaDaMoldura(): FilaDaMoldura {
  const { empresa } = useLoja();
  const agora = useAgora();
  const consulta = useFilaDoDia(empresa, { intervalo: INTERVALO_DA_MOLDURA_MS, agora });
  const { inicio, fim } = janelaDoDiaOperacional(agora, empresa.viradaDiaHora, empresa.fuso);
  const { data, dataUpdatedAt } = consulta;
  const fila = useMemo(
    () => (data ? filaDoBalcao(data, agora, empresa.prazoMinutos, { inicio, fim }) : null),
    [data, agora, empresa.prazoMinutos, inicio, fim],
  );
  return { fila, atualizadoEm: dataUpdatedAt ? new Date(dataUpdatedAt).toISOString() : null };
}

type Atrasado = LinhaDaFila<PedidoNaLista>;

function NomeDoAtrasado({ linha, curto }: { linha: Atrasado; curto: boolean }) {
  const { pedido } = linha;
  const nome = curto ? primeiroNome(pedido.clienteNome) : pedido.clienteNome;
  return (
    <Link to={`/pedidos/${pedido.id}`} className={css.nome}>
      {nome} #{pedido.numero}
    </Link>
  );
}

/**
 * "João Pereira #1089, atrasou 28 min · …"; no pico ou em tela média, "Larissa #2205, 21 min · …";
 * curto, só os nomes.
 */
function ListaDeAtrasados({
  atrasados,
  curto,
  medio = false,
}: {
  atrasados: Atrasado[];
  curto: boolean;
  medio?: boolean;
}) {
  const compacto = !curto && (medio || atrasados.length > ATRASADOS_POR_EXTENSO);
  return (
    <>
      <b>{plural(atrasados.length, 'atrasado', 'atrasados')}:</b>{' '}
      {atrasados.map((linha, i) => (
        <Fragment key={linha.pedido.id}>
          {i > 0 && ' · '}
          <NomeDoAtrasado linha={linha} curto={curto || compacto} />
          {!curto &&
            (compacto ? `, ${linha.relogio.minutos} min` : `, ${textoDoRelogio(linha.relogio)}`)}
        </Fragment>
      ))}
    </>
  );
}

/** O que o leitor de tela anuncia: muda quando muda quem atrasou, não a cada minuto. */
function anuncioDosAtrasados(atrasados: Atrasado[]): string {
  if (atrasados.length === 0) return 'Nada atrasado';
  const nomes = atrasados.map((l) => l.pedido.clienteNome).join(', ');
  return `${plural(atrasados.length, 'atrasado', 'atrasados')}: ${nomes}`;
}

function SemConexao({ desde, hora, fuso }: { desde: Date; hora: string | null; fuso: string }) {
  const { tentarAgora } = useConexao();
  const quando = formatarHora(desde.toISOString(), fuso);
  const lista = hora
    ? `A lista é a das ${hora} e os minutos continuam contando.`
    : 'A lista aparece quando a internet voltar.';
  return (
    <div className={`${css.linha} ${css.semConexao}`}>
      <div className={css.faixa}>
        <p className={css.esquerda} role="status">
          <Icone nome="semRede" tamanho={16} />
          <span className={css.texto}>
            <b>Sem conexão desde {quando}.</b>
            <span className={css.longo}> {lista}</span>
          </span>
        </p>
        <button type="button" className={css.tentar} onClick={tentarAgora}>
          Tentar agora
        </button>
      </div>
    </div>
  );
}

export function LinhaDeEstado() {
  const { empresa } = useLoja();
  const { online, desde } = useConexao();
  const { fila, atualizadoEm } = useFilaDaMoldura();
  const hora = atualizadoEm ? formatarHora(atualizadoEm, empresa.fuso) : null;

  if (!online) return <SemConexao desde={desde ?? new Date()} hora={hora} fuso={empresa.fuso} />;

  const atrasados = fila?.atrasados ?? [];
  return (
    <div className={css.linha}>
      <div className={css.faixa}>
        <p className="sr-only" aria-live="polite">
          {fila ? anuncioDosAtrasados(atrasados) : ''}
        </p>
        {fila && atrasados.length > 0 && (
          <p className={`${css.esquerda} ${css.atrasados}`}>
            <Icone nome="alerta" tamanho={15} />
            <span className={`${css.texto} ${css.extenso}`}>
              <ListaDeAtrasados atrasados={atrasados} curto={false} />
            </span>
            <span className={`${css.texto} ${css.medio}`}>
              <ListaDeAtrasados atrasados={atrasados} curto={false} medio />
            </span>
            <span className={`${css.texto} ${css.curto}`}>
              <ListaDeAtrasados atrasados={atrasados} curto />
            </span>
          </p>
        )}
        {fila && atrasados.length === 0 && (
          <p className={css.esquerda}>
            <Icone nome="check" tamanho={15} />
            <span className={css.texto}>Nada atrasado</span>
          </p>
        )}
        <p className={css.vivo}>
          <span className={css.prazo}>Prazo {empresa.prazoMinutos} min</span>
          {hora && (
            <>
              <span className={css.prazo}> · </span>
              <span className={css.longo}>atualizado às </span>
              {hora}
              <i className={css.ponto} aria-hidden="true" />
            </>
          )}
        </p>
      </div>
    </div>
  );
}
